import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { PopoverController } from 'providers/popover-controller';
import { DateTime } from 'luxon';

import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatWeekdayDayMonthShort } from '@cf-shared/date-format';
export interface CopyMealsPopoverInput {
  /** Description of what is being copied (e.g., "Breakfast" or "Meals 1-4") */
  mealDescription: string;
  /** Days available to copy to (excluding current day) */
  availableDays: DateTime[];
  /** Index of current day (to exclude from selection) */
  currentDayIndex: number;
}

export interface CopyMealsPopoverResult {
  /** Indices of days selected for copying */
  selectedDayIndices: number[];
}

interface DayOption {
  index: number;
  date: DateTime;
  label: string;
  checked: boolean;
}

@Component({
  selector: 'copy-meals-popover',
  templateUrl: 'copy-meals-popover.html',
  host: { 'class': 'copy-meals-popover' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CopyMealsPopover extends CfPopoverBase {
  protected LL = LL;
  @Input() input: CopyMealsPopoverInput;

  dayOptions: DayOption[] = [];
  selectAll = false;
  private cancelled = false;

  constructor(
    private popoverController: PopoverController,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  afterAnimationFinished(): void {
    this.initializeDayOptions();
  }

  private initializeDayOptions() {
    if (!this.input?.availableDays) {
      return;
    }

    this.dayOptions = this.input.availableDays.map((date, index) => ({
      index,
      date,
      label: formatWeekdayDayMonthShort(date, BOOT_LOCALE),
      checked: false
    }));

    this.cdRef.markForCheck();
  }

  onWillDismiss(data: any): void {
    if (this.cancelled) {
      data.resultData = null;
    } else {
      const selectedIndices = this.dayOptions
        .filter(opt => opt.checked)
        .map(opt => opt.index);

      data.resultData = selectedIndices.length > 0
        ? { selectedDayIndices: selectedIndices } as CopyMealsPopoverResult
        : null;
    }
  }

  handleSelectAllChange(checked: boolean) {
    this.selectAll = checked;
    for (let option of this.dayOptions) {
      option.checked = checked;
    }
    this.cdRef.markForCheck();
  }

  handleDayChange(option: DayOption, checked: boolean) {
    option.checked = checked;
    // Update selectAll based on whether all are checked
    this.selectAll = this.dayOptions.every(opt => opt.checked);
    this.cdRef.markForCheck();
  }

  handleCancel() {
    this.cancelled = true;
    void this.popoverController.dismiss(this);
  }

  handleCopy() {
    this.cancelled = false;
    void this.popoverController.dismiss(this);
  }

  handleClose() {
    this.cancelled = true;
    void this.popoverController.dismiss(this);
  }

  get hasSelection(): boolean {
    return this.dayOptions.some(opt => opt.checked);
  }

  // Split days into two columns for display
  get leftColumnDays(): DayOption[] {
    const midpoint = Math.ceil(this.dayOptions.length / 2);
    return this.dayOptions.slice(0, midpoint);
  }

  get rightColumnDays(): DayOption[] {
    const midpoint = Math.ceil(this.dayOptions.length / 2);
    return this.dayOptions.slice(midpoint);
  }
}
